import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { ListGroup, Form } from 'react-bootstrap';
import { useDarkMode } from 'Hooks';
import RestClient from 'Services/RestClientService';
import PropTypes from 'prop-types';

const MenuItem = ({ href, icon, text, count, isSidebarOpen, checkActiveLink }) => {
  const isActive = checkActiveLink(href.substring(1));
  return (
    <ListGroup.Item
      as={Link}
      to={href}
      action
      className={isActive ? 'active' : 'inactive'}
      style={{
        borderLeft: isActive ? '3px solid #0066cc' : '3px solid transparent',
        backgroundColor: isActive ? '#e7f3ff' : 'transparent',
        color: isActive ? '#0066cc' : '#6c757d',
      }}
    >
      <i className={`${icon} ${isSidebarOpen ? 'me-2' : ''}`} />
      <span className="menu-text">
        {text} {count !== undefined && <small>({count})</small>}
      </span>
    </ListGroup.Item>
  );
};

const SideBar = ({ isSidebarOpen, toggleSidebar }) => {
  const sidebarRef = useRef(null);
  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles || [];
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [ticketCounts, setTicketCounts] = useState({
    totalNew: 0,
    totalPendinghr: 0,
    totalPendingbtt: 0,
    totalPendingdes: 0,
    totalConfirmed: 0,
    totalAllStatus: 0,
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await RestClient.Get('flight-ticket/count');
        setTicketCounts(response?.data || {});
      } catch (error) {
        console.error('Error fetching ticket counts:', error);
      }
    };
    fetchCounts();
  }, []);

  const location = useLocation();

  const checkActiveLink = (activeLink) => {
    const pathName = location.pathname;
    if (pathName === '/' && activeLink === 'new') return true;
    return pathName.includes(activeLink);
  };

  return (
    <CSSTransition in={isSidebarOpen} timeout={300} classNames="sidebar-transition" nodeRef={sidebarRef}>
      <div className={`sidebar ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`} ref={sidebarRef}>
        <div className="sidebar-header">
          <button onClick={toggleSidebar} className="sidebar-toggle-button">
            ☰
          </button>
          {isSidebarOpen && (
            <div className="sidebar-logo">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 136 60">
                <path
                  style={{ fill: '#d40e14' }}
                  d="M102.62,29.4a2.09,2.09,0,0,1,.07.6,8.63,8.63,0,0,1-.12,1.27,4.14,4.14,0,0,1-.35,1.24.92.92,0,0,1-.54.47,3.14,3.14,0,0,1-.91.11h-5V50.31a1.74,1.74,0,0,1-.08.62.93.93,0,0,1-.28.37,4,4,0,0,1-2.14.36,4.19,4.19,0,0,1-2.16-.35,1.15,1.15,0,0,1-.38-1V33.14H85.12a2.09,2.09,0,0,1-1-.16.61.61,0,0,1-.28-.35,2.16,2.16,0,0,1-.07-.62,7.8,7.8,0,0,1,.12-1.4,3.56,3.56,0,0,1,.35-1.09.93.93,0,0,1,.54-.48,3.14,3.14,0,0,1,.91-.11h15.65a4,4,0,0,1,.66,0,1,1,0,0,1,.35.12A.63.63,0,0,1,102.62,29.4Zm17.82-.27a.78.78,0,0,0-.3.35,1.83,1.83,0,0,0-.08.63v14a5.33,5.33,0,0,1-.29,1.69,2.65,2.65,0,0,1-1.26,1.44,5.07,5.07,0,0,1-2.57.57,6,6,0,0,1-1.89-.27,3.29,3.29,0,0,1-1.28-.75,2.93,2.93,0,0,1-.73-1.17,4.7,4.7,0,0,1-.23-1.51v-14a1.18,1.18,0,0,0-.35-1,6.77,6.77,0,0,0-4.29,0,.78.78,0,0,0-.3.35,1.83,1.83,0,0,0-.08.63v14a9.22,9.22,0,0,0,.52,3.14,6.37,6.37,0,0,0,1.63,2.5,7.7,7.7,0,0,0,2.89,1.66,14.85,14.85,0,0,0,8.3,0l0,0a7.7,7.7,0,0,0,2.85-1.65,6.37,6.37,0,0,0,1.63-2.5,9.21,9.21,0,0,0,.52-3.14v-14a1.18,1.18,0,0,0-.35-1A6.77,6.77,0,0,0,120.44,29.13Zm15,.36a.88.88,0,0,0-.29-.35,4.09,4.09,0,0,0-2.13-.35,4.31,4.31,0,0,0-2.16.35.88.88,0,0,0-.29.35,1.68,1.68,0,0,0-.09.63v20.2a1.21,1.21,0,0,0,.38,1,4.19,4.19,0,0,0,2.16.35,3.91,3.91,0,0,0,2.13-.37,1,1,0,0,0,.29-.37,1.62,1.62,0,0,0,.09-.62V30.12A1.69,1.69,0,0,0,135.44,29.49Zm-73-3.13c-2.35-.58-3,.25-5.2,4.84-13,27.73-36.8,19.34-40.87-6.41L22,24.38c3.09-.22,3.31-2.12,2.74-4.85-.54-2.45-1.8-3-3.54-3H3.53C1.26,16.53,0,17.74,0,21.14c0,1.72.15,4.8,3.83,4.53l4.45-.3c7.4,45.66,45.1,42.23,56.3,8C66.11,28.8,65.68,27.15,62.49,26.36ZM67.65,0a6.54,6.54,0,1,0,6.54,6.54A6.54,6.54,0,0,0,67.65,0Z"
                />
              </svg>
              <span className="sidebar-app-name">Fly2Work</span>
            </div>
          )}
        </div>
        <ListGroup variant="flush" className="sidebar-menu">
          <MenuItem
            href="/dashboard"
            icon="fa fa-bar-chart"
            text="Dashboard"
            isSidebarOpen={isSidebarOpen}
            checkActiveLink={checkActiveLink}
          />
          <MenuItem
            href="/new"
            icon="fa fa-user-plus"
            text="New"
            count={ticketCounts.totalNew}
            isSidebarOpen={isSidebarOpen}
            checkActiveLink={checkActiveLink}
          />
          <MenuItem
            href="/pendinghr"
            icon="fa fa-handshake-o"
            text="Pending HR"
            count={ticketCounts.totalPendinghr}
            isSidebarOpen={isSidebarOpen}
            checkActiveLink={checkActiveLink}
          />
          <MenuItem
            href="/pendingbtt"
            icon="fa fa-exchange"
            text="Pending BTT"
            count={ticketCounts.totalPendingbtt}
            isSidebarOpen={isSidebarOpen}
            checkActiveLink={checkActiveLink}
          />
          <MenuItem
            href="/pendingdes"
            icon="fa fa-fort-awesome"
            text="Pending DES"
            count={ticketCounts.totalPendingdes}
            isSidebarOpen={isSidebarOpen}
            checkActiveLink={checkActiveLink}
          />
          <MenuItem
            href="/confirmed"
            icon="fa fa-plane"
            text="Confirmed"
            count={ticketCounts.totalConfirmed}
            isSidebarOpen={isSidebarOpen}
            checkActiveLink={checkActiveLink}
          />
          <MenuItem
            href="/all"
            icon="fa fa-users"
            text="Overview"
            count={ticketCounts.totalAllStatus}
            isSidebarOpen={isSidebarOpen}
            checkActiveLink={checkActiveLink}
          />
          {userRoles.includes('DS_F2W_Business_Travel_Team') && (
            <MenuItem
              href="/settings"
              icon="fa fa-gear"
              text="Settings"
              isSidebarOpen={isSidebarOpen}
              checkActiveLink={checkActiveLink}
            />
          )}
        </ListGroup>
        <div className="sidebar-footer">
          <ListGroup variant="flush">
            <ListGroup.Item
              action
              onClick={toggleDarkMode}
              style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              <i className={`fa fa-${darkMode ? 'sun-o' : 'moon-o'}`} />
              <span>Dark Mode</span>
              {isSidebarOpen && (
                <Form.Check
                  type="switch"
                  checked={darkMode}
                  onChange={toggleDarkMode}
                  className="ms-auto d-inline-block"
                  style={{ pointerEvents: 'none' }}
                />
              )}
            </ListGroup.Item>
          </ListGroup>
        </div>
      </div>
    </CSSTransition>
  );
};

SideBar.propTypes = {
  isSidebarOpen: PropTypes.bool.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
};

export default SideBar;
